import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../layouts/Header';
import { Card, Row, Col, Input, FormGroup, Label, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Button, Badge, Pagination, Dropdown, Form } from 'react-bootstrap';
import {
    getAllReviews,
    parseReviewsResponse,
    updateReviewStatus,
    deleteReview,
    createAdminReview,
    formatProductPickerLabel,
} from '../../helper/review_helper';
import { getAllProducts } from '../../helper/product_helper';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import DataTable from 'react-data-table-component';
import DataTableSkeleton from '../../components/DataTableSkeleton';
import Footer from '../../layouts/Footer';
import AdminEmptyState from '../../components/admin/AdminEmptyState';
import ConfirmModal from '../../components/ConfirmModal';
import { dataTableCustomStyles } from '../../components/admin/dataTableConfig';
import StarRating from '../../components/admin/StarRating';
import AdminRowActions, { AdminActionButton } from '../../components/admin/AdminRowActions';
import moment from 'moment';

function getAdminToken(userState) {
    const nested = userState?.user || userState;
    return nested?.token || localStorage.getItem('adminToken') || '';
}

function getStatusBadgeVariant(status) {
    switch (String(status).toLowerCase()) {
        case 'approved':
            return 'success';
        case 'hidden':
            return 'danger';
        case 'pending':
        default:
            return 'warning';
    }
}

function Reviews() {
    const userState = useSelector(state => state.user);
    const token = getAdminToken(userState);

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalReviews, setTotalReviews] = useState(0);
    const [perPage, setPerPage] = useState(10);

    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [productFilter, setProductFilter] = useState('');
    const [ratingFilter, setRatingFilter] = useState('');
    const [verifiedFilter, setVerifiedFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmData, setConfirmData] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    const [products, setProducts] = useState([]);
    const [productsLoading, setProductsLoading] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [addSubmitting, setAddSubmitting] = useState(false);
    const [addForm, setAddForm] = useState({
        productId: '',
        name: '',
        rating: 5,
        comment: '',
    });

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        let cancelled = false;
        const loadProducts = async () => {
            setProductsLoading(true);
            try {
                const res = await getAllProducts({ page: 1, limit: 500 });
                let list = [];
                if (res?.success && Array.isArray(res?.data?.products)) {
                    list = res.data.products;
                } else if (Array.isArray(res?.products)) {
                    list = res.products;
                } else if (Array.isArray(res?.data)) {
                    list = res.data;
                }
                if (!cancelled) {
                    setProducts(list);
                }
            } catch (err) {
                console.error('Error loading products for reviews:', err);
                if (!cancelled) {
                    toast.error('Failed to load products');
                    setProducts([]);
                }
            } finally {
                if (!cancelled) {
                    setProductsLoading(false);
                }
            }
        };
        loadProducts();
        return () => {
            cancelled = true;
        };
    }, []);

    const fetchReviews = useCallback(async () => {
        if (!token) {
            toast.error('Not authorized');
            return;
        }

        setLoading(true);
        try {
            const params = {
                page: currentPage,
                limit: perPage,
            };

            if (debouncedSearch) params.search = debouncedSearch;
            if (productFilter) params.productId = productFilter;
            if (ratingFilter) params.rating = ratingFilter;
            if (verifiedFilter) params.verified = verifiedFilter;
            if (statusFilter) params.status = statusFilter;
            if (startDate) params.startDate = startDate;
            if (endDate) params.endDate = endDate;

            const response = await getAllReviews(params, token);
            const { reviews, pagination, error } = parseReviewsResponse(response);

            if (error) {
                toast.error(error);
                return;
            }

            setData(reviews);
            if (pagination) {
                setTotalPages(pagination.pages || 1);
                setTotalReviews(pagination.total || reviews.length);
            } else {
                setTotalPages(1);
                setTotalReviews(reviews.length);
            }
        } catch (error) {
            console.error('Error fetching reviews:', error);
            toast.error(error?.message || 'Failed to load reviews');
        } finally {
            setLoading(false);
        }
    }, [token, currentPage, perPage, debouncedSearch, productFilter, ratingFilter, verifiedFilter, statusFilter, startDate, endDate]);

    useEffect(() => {
        fetchReviews();
    }, [fetchReviews]);

    const handleStatusChange = async (reviewId, newStatus) => {
        if (!reviewId || !newStatus) return;

        setActionLoading(true);
        try {
            const response = await updateReviewStatus(reviewId, newStatus, token);
            if (response.success !== false) {
                toast.success(`Review ${newStatus}`);
                fetchReviews();
            } else {
                toast.error(response.message || 'Failed to update status');
            }
        } catch (error) {
            console.error('Error updating review status:', error);
            toast.error(error?.message || 'Failed to update status');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async (reviewId) => {
        if (!reviewId) return;

        setConfirmData({
            title: 'Delete Review',
            message: 'Are you sure you want to delete this review? This action cannot be undone.',
            onConfirm: async () => {
                setActionLoading(true);
                try {
                    const response = await deleteReview(reviewId, token);
                    if (response.success !== false) {
                        toast.success('Review deleted successfully');
                        fetchReviews();
                    } else {
                        toast.error(response.message || 'Failed to delete review');
                    }
                } catch (error) {
                    console.error('Error deleting review:', error);
                    toast.error(error?.message || 'Failed to delete review');
                } finally {
                    setActionLoading(false);
                    setConfirmOpen(false);
                }
            },
        });
        setConfirmOpen(true);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePerPageChange = (newPerPage) => {
        setPerPage(newPerPage);
        setCurrentPage(1);
    };

    const openAddReviewModal = () => {
        setAddForm({
            productId: productFilter || '',
            name: '',
            rating: 5,
            comment: '',
        });
        setShowAddModal(true);
    };

    const handleAddReviewSubmit = async (e) => {
        e.preventDefault();
        if (!addForm.productId) {
            toast.error('Please select a product');
            return;
        }
        if (!addForm.name.trim()) {
            toast.error('Customer name is required');
            return;
        }
        if (!addForm.comment.trim()) {
            toast.error('Review comment is required');
            return;
        }

        setAddSubmitting(true);
        try {
            const response = await createAdminReview(
                {
                    productId: addForm.productId,
                    name: addForm.name.trim(),
                    rating: addForm.rating,
                    comment: addForm.comment.trim(),
                },
                token
            );

            if (response?.success === false) {
                toast.error(response.message || 'Failed to add product review');
                return;
            }

            toast.success(response?.message || 'Product review added — visible on storefront after approval');
            setShowAddModal(false);
            if (addForm.productId && addForm.productId !== productFilter) {
                setProductFilter(addForm.productId);
                setCurrentPage(1);
            } else {
                fetchReviews();
            }
        } catch (error) {
            console.error('Error creating admin review:', error);
            toast.error(error?.message || 'Failed to add product review');
        } finally {
            setAddSubmitting(false);
        }
    };

    const clearFilters = () => {
        setSearchTerm('');
        setProductFilter('');
        setRatingFilter('');
        setVerifiedFilter('');
        setStatusFilter('');
        setStartDate('');
        setEndDate('');
        setCurrentPage(1);
    };

    const hasActiveFilters = Boolean(
        debouncedSearch ||
        productFilter ||
        ratingFilter ||
        verifiedFilter ||
        statusFilter ||
        startDate ||
        endDate ||
        perPage !== 10
    );

    const columns = useMemo(() => [
        {
            name: '#',
            cell: (row, index) => (
                <span className="text-muted small">{(currentPage - 1) * perPage + index + 1}</span>
            ),
            width: '56px',
        },
        {
            name: 'Product',
            minWidth: '180px',
            grow: 1,
            cell: row => {
                const productName = row.product?.name || row.product?.title || row.productName || 'Unknown Product';
                const productId = row.product?._id || row.productId;
                return (
                    <div className="py-1">
                        <div className="fw-semibold text-truncate" style={{ maxWidth: '160px' }} title={productName}>
                            {productName}
                        </div>
                        {productId && (
                            <div className="text-muted small text-truncate">
                                ID: {productId.slice(-8)}
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            name: 'User',
            minWidth: '150px',
            cell: row => {
                const userName = row.user?.name || row.userName || 'Anonymous';
                const userEmail = row.user?.email || row.userEmail;
                return (
                    <div className="py-1">
                        <div className="fw-medium text-truncate" style={{ maxWidth: '130px' }} title={userName}>
                            {userName}
                        </div>
                        {userEmail && (
                            <div className="text-muted small text-truncate" title={userEmail}>
                                {userEmail}
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            name: 'Rating',
            width: '120px',
            cell: row => <StarRating value={row.rating || 0} max={5} />,
        },
        {
            name: 'Comment',
            minWidth: '250px',
            grow: 2,
            cell: row => (
                <div className="text-truncate py-1" style={{ maxWidth: '240px' }} title={row.comment || row.review}>
                    {row.comment || row.review || '—'}
                </div>
            ),
        },
        {
            name: 'Verified',
            width: '90px',
            center: true,
            cell: row => (
                row.verified || row.isVerified ? (
                    <Badge bg="info" className="small">
                        <i className="ri-shield-check-line me-1" />
                        Verified
                    </Badge>
                ) : (
                    <span className="text-muted small">—</span>
                )
            ),
        },
        {
            name: 'Status',
            width: '100px',
            cell: row => (
                <Badge bg={getStatusBadgeVariant(row.status)} className="small">
                    {String(row.status || 'pending').charAt(0).toUpperCase() + String(row.status || 'pending').slice(1)}
                </Badge>
            ),
        },
        {
            name: 'Date',
            width: '140px',
            cell: row => (
                <span className="small">
                    {row.createdAt ? moment(row.createdAt).format('DD MMM YYYY') : '—'}
                </span>
            ),
        },
        {
            name: 'Actions',
            width: '140px',
            cell: row => (
                <Dropdown align="end">
                    <Dropdown.Toggle
                        variant="link"
                        className="text-decoration-none p-0 text-dark"
                        disabled={actionLoading}
                        size="sm"
                    >
                        <i className="ri-more-2-fill" style={{ fontSize: '18px' }} />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        {row.status !== 'approved' && (
                            <Dropdown.Item
                                onClick={() => handleStatusChange(row._id, 'approved')}
                                disabled={actionLoading}
                            >
                                <i className="ri-check-line me-2 text-success" />
                                Approve
                            </Dropdown.Item>
                        )}
                        {row.status !== 'hidden' && (
                            <Dropdown.Item
                                onClick={() => handleStatusChange(row._id, 'hidden')}
                                disabled={actionLoading}
                            >
                                <i className="ri-eye-off-line me-2 text-warning" />
                                Hide
                            </Dropdown.Item>
                        )}
                        {row.status !== 'pending' && (
                            <Dropdown.Item
                                onClick={() => handleStatusChange(row._id, 'pending')}
                                disabled={actionLoading}
                            >
                                <i className="ri-time-line me-2 text-secondary" />
                                Set Pending
                            </Dropdown.Item>
                        )}
                        <Dropdown.Divider />
                        <Dropdown.Item
                            onClick={() => handleDelete(row._id)}
                            disabled={actionLoading}
                            className="text-danger"
                        >
                            <i className="ri-delete-bin-line me-2" />
                            Delete
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            ),
        },
    ], [currentPage, perPage, actionLoading]);

    const paginationComponent = useMemo(() => {
        if (totalPages <= 1) return null;

        const pages = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage < maxVisible - 1) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <Pagination.Item
                    key={i}
                    active={i === currentPage}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        return (
            <Pagination className="mb-0">
                <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                {startPage > 1 && (
                    <>
                        <Pagination.Item onClick={() => handlePageChange(1)}>1</Pagination.Item>
                        {startPage > 2 && <Pagination.Ellipsis disabled />}
                    </>
                )}
                {pages}
                {endPage < totalPages && (
                    <>
                        {endPage < totalPages - 1 && <Pagination.Ellipsis disabled />}
                        <Pagination.Item onClick={() => handlePageChange(totalPages)}>{totalPages}</Pagination.Item>
                    </>
                )}
                <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
            </Pagination>
        );
    }, [currentPage, totalPages]);

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="d-md-flex align-items-center justify-content-between mb-4">
                    <div>
                        <h4 className="main-title mb-0">Product Reviews</h4>
                        <p className="text-muted mb-0 mt-1">
                            Add and moderate reviews shown on product pages (storefront collection)
                        </p>
                    </div>
                    <div className="mt-3 mt-md-0">
                        <Button variant="primary" onClick={openAddReviewModal}>
                            <i className="ri-add-line me-1" />
                            Add product review
                        </Button>
                    </div>
                </div>

                <Card className="card-one mb-4">
                    <div className="card-body">
                        <h6 className="mb-3 fw-semibold">Filters</h6>
                        <Row className="g-3">
                            <Col md={3}>
                                <FormGroup>
                                    <Label className="small fw-medium mb-1">Product</Label>
                                    <Input
                                        type="select"
                                        value={productFilter}
                                        onChange={(e) => {
                                            setProductFilter(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                        bsSize="sm"
                                        disabled={productsLoading}
                                    >
                                        <option value="">
                                            {productsLoading ? 'Loading products…' : 'All products'}
                                        </option>
                                        {products.map((product) => (
                                            <option key={product._id} value={product._id}>
                                                {formatProductPickerLabel(product)}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Label className="small fw-medium mb-1">Search</Label>
                                    <Input
                                        type="text"
                                        placeholder="Search reviews..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        bsSize="sm"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={2}>
                                <FormGroup>
                                    <Label className="small fw-medium mb-1">Rating</Label>
                                    <Input
                                        type="select"
                                        value={ratingFilter}
                                        onChange={(e) => setRatingFilter(e.target.value)}
                                        bsSize="sm"
                                    >
                                        <option value="">All Ratings</option>
                                        <option value="5">5 Stars</option>
                                        <option value="4">4 Stars</option>
                                        <option value="3">3 Stars</option>
                                        <option value="2">2 Stars</option>
                                        <option value="1">1 Star</option>
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={2}>
                                <FormGroup>
                                    <Label className="small fw-medium mb-1">Verified</Label>
                                    <Input
                                        type="select"
                                        value={verifiedFilter}
                                        onChange={(e) => setVerifiedFilter(e.target.value)}
                                        bsSize="sm"
                                    >
                                        <option value="">All</option>
                                        <option value="true">Verified</option>
                                        <option value="false">Not Verified</option>
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={2}>
                                <FormGroup>
                                    <Label className="small fw-medium mb-1">Status</Label>
                                    <Input
                                        type="select"
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                        bsSize="sm"
                                    >
                                        <option value="">All Status</option>
                                        <option value="pending">Pending</option>
                                        <option value="approved">Approved</option>
                                        <option value="hidden">Hidden</option>
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Label className="small fw-medium mb-1">Start Date</Label>
                                    <Input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        bsSize="sm"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Label className="small fw-medium mb-1">End Date</Label>
                                    <Input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        bsSize="sm"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={2}>
                                <FormGroup>
                                    <Label className="small fw-medium mb-1">Per Page</Label>
                                    <Input
                                        type="select"
                                        value={perPage}
                                        onChange={(e) => handlePerPageChange(Number(e.target.value))}
                                        bsSize="sm"
                                    >
                                        <option value="10">10</option>
                                        <option value="25">25</option>
                                        <option value="50">50</option>
                                        <option value="100">100</option>
                                    </Input>
                                </FormGroup>
                            </Col>
                            {hasActiveFilters && (
                                <Col md={2} className="d-flex align-items-end">
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={clearFilters}
                                        className="w-100"
                                    >
                                        <i className="ri-close-line me-1" />
                                        Clear Filters
                                    </Button>
                                </Col>
                            )}
                        </Row>
                    </div>
                </Card>

                <Card className="card-one">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <h6 className="mb-0 fw-semibold">Reviews List</h6>
                                <p className="text-muted small mb-0 mt-1">
                                    Total: {totalReviews} review{totalReviews !== 1 ? 's' : ''}
                                </p>
                            </div>
                        </div>

                        {loading ? (
                            <DataTableSkeleton columns={9} rows={perPage} />
                        ) : data.length === 0 ? (
                            <AdminEmptyState
                                icon="ri-chat-quote-line"
                                title={hasActiveFilters ? 'No reviews match your filters' : 'No reviews yet'}
                                message={hasActiveFilters ? 'Try adjusting your filters' : 'Reviews will appear here once customers submit them'}
                            />
                        ) : (
                            <>
                                <DataTable
                                    columns={columns}
                                    data={data}
                                    customStyles={dataTableCustomStyles}
                                    noDataComponent={
                                        <AdminEmptyState
                                            icon="ri-chat-quote-line"
                                            title="No reviews found"
                                            message="Reviews will appear here once available"
                                        />
                                    }
                                />
                                {paginationComponent && (
                                    <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                                        <div className="text-muted small">
                                            Showing {Math.min((currentPage - 1) * perPage + 1, totalReviews)} to{' '}
                                            {Math.min(currentPage * perPage, totalReviews)} of {totalReviews}
                                        </div>
                                        {paginationComponent}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </Card>

                <Footer />
            </div>

            <Modal isOpen={showAddModal} toggle={() => !addSubmitting && setShowAddModal(false)} size="lg">
                <ModalHeader toggle={() => !addSubmitting && setShowAddModal(false)}>
                    Add product review
                </ModalHeader>
                <Form onSubmit={handleAddReviewSubmit}>
                    <ModalBody>
                        <p className="text-muted small">
                            Creates a review in the product review collection (same data as the storefront).
                            Admin-added reviews are not marked as verified buyer purchases.
                        </p>
                        <Form.Group className="mb-3">
                            <Form.Label>
                                Product <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Select
                                value={addForm.productId}
                                onChange={(e) => setAddForm({ ...addForm, productId: e.target.value })}
                                required
                                disabled={productsLoading || !products.length}
                            >
                                <option value="">
                                    {productsLoading
                                        ? 'Loading products…'
                                        : products.length
                                            ? 'Select product (name & size)'
                                            : 'No products available'}
                                </option>
                                {products.map((product) => (
                                    <option key={product._id} value={product._id}>
                                        {formatProductPickerLabel(product)}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Customer name <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={addForm.name}
                                        onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                                        placeholder="Name shown on storefront"
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Rating <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        value={addForm.rating}
                                        onChange={(e) =>
                                            setAddForm({ ...addForm, rating: parseInt(e.target.value, 10) })
                                        }
                                        required
                                    >
                                        <option value="5">5 stars</option>
                                        <option value="4">4 stars</option>
                                        <option value="3">3 stars</option>
                                        <option value="2">2 stars</option>
                                        <option value="1">1 star</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-0">
                            <Form.Label>
                                Comment <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                value={addForm.comment}
                                onChange={(e) => setAddForm({ ...addForm, comment: e.target.value })}
                                placeholder="Review text for the product page"
                                required
                            />
                        </Form.Group>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            variant="secondary"
                            onClick={() => setShowAddModal(false)}
                            disabled={addSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={addSubmitting}>
                            {addSubmitting ? 'Saving…' : 'Save product review'}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>

            <ConfirmModal
                show={confirmOpen}
                onHide={() => setConfirmOpen(false)}
                onConfirm={() => {
                    if (confirmData?.onConfirm) {
                        confirmData.onConfirm();
                    }
                }}
                title={confirmData?.title || 'Confirm Action'}
                message={confirmData?.message || 'Are you sure?'}
                confirmText="Delete"
                confirmVariant="danger"
                loading={actionLoading}
            />
        </React.Fragment>
    );
}

export default Reviews;
